import type { ReactNode } from 'react';
import { Card, Money, StatusBadge } from '@/components/ui';
import { formatNumber, str } from '@/utils/format';

export function EntityList({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`list-stack ${className}`.trim()}>{children}</div>;
}

export function EntityCard({
  title,
  meta,
  meta2,
  amount,
  amountSuffix,
  status,
  actions,
  onClick,
  leading,
  className = 'list-card',
}: {
  title: ReactNode;
  meta?: ReactNode;
  meta2?: ReactNode;
  amount?: unknown;
  amountSuffix?: string;
  status?: unknown;
  actions?: ReactNode;
  onClick?: () => void;
  leading?: ReactNode;
  className?: string;
}) {
  return (
    <Card className={className} onClick={onClick}>
      <div className="list-row">
        <div className="list-main">
          {leading ? <div className="list-leading">{leading}</div> : null}
          <div>
            <p className="card-title">{title}</p>
            {meta ? <p className="card-meta">{meta}</p> : null}
            {meta2 ? <p className="card-meta">{meta2}</p> : null}
          </div>
        </div>
        {(amount !== undefined || status !== undefined) && (
          <div className="list-end">
            {amount !== undefined ? <Money value={amount} suffix={amountSuffix ?? 'ل.س'} /> : null}
            {status !== undefined ? <StatusBadge status={status} /> : null}
          </div>
        )}
      </div>
      {actions ? <div className="btn-row">{actions}</div> : null}
    </Card>
  );
}

export function KeyValueGrid({
  items,
}: {
  items: Array<{ label: string; value: ReactNode }>;
}) {
  return (
    <div className="detail-grid section-card">
      {items.map((item) => (
        <div className="kv-row" key={item.label}>
          <span className="kv-label">{item.label}</span>
          <span className="kv-value">{item.value}</span>
        </div>
      ))}
    </div>
  );
}

export function ObjectFields({
  data,
  prefer = [],
}: {
  data: Record<string, unknown> | null | undefined;
  prefer?: string[];
}) {
  if (!data) return null;
  const keys = [
    ...prefer.filter((k) => k in data),
    ...Object.keys(data).filter((k) => !prefer.includes(k)),
  ].slice(0, 24);

  return (
    <KeyValueGrid
      items={keys.map((key) => {
        const value = data[key];
        let display: ReactNode = '—';
        if (value === null || value === undefined) display = '—';
        else if (typeof value === 'boolean') display = value ? 'نعم' : 'لا';
        else if (typeof value === 'number') {
          display = (
            <span className="num" dir="ltr" lang="en">
              {formatNumber(value)}
            </span>
          );
        } else if (typeof value === 'string') {
          display = /[0-9٠-٩۰-۹]/.test(value) ? (
            <span className="num" dir="ltr" lang="en">
              {str(value)}
            </span>
          ) : (
            value
          );
        }
        else display = <code className="inline-code">{JSON.stringify(value)}</code>;
        return { label: key, value: display };
      })}
    />
  );
}

export function ProductTile({
  title,
  price,
  suffix = 'ل.س',
  network,
  onClick,
  className = 'product-card',
}: {
  title: string;
  price: unknown;
  suffix?: string;
  network?: ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <Card className={className} onClick={onClick}>
      {network ? <div className="product-network">{network}</div> : null}
      <p className="card-title">{title}</p>
      <p className="product-card-price">
        <Money value={price} suffix={suffix} />
      </p>
    </Card>
  );
}

export function TabsRow<T extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: Array<{ key: T; label: string }>;
  value: T;
  onChange: (key: T) => void;
}) {
  return (
    <div className="tabs-row">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          className={value === tab.key ? 'active' : ''}
          onClick={() => onChange(tab.key)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export function ShortcutGrid({
  items,
}: {
  items: Array<{ key: string; title: string; text: string; icon?: ReactNode; onClick: () => void }>;
}) {
  return (
    <section className="vi-admin-shortcuts">
      {items.map((item) => (
        <button key={item.key} type="button" onClick={item.onClick}>
          {item.icon}
          <div>
            <strong>{item.title}</strong>
            <span>{item.text}</span>
          </div>
        </button>
      ))}
    </section>
  );
}

export function MetricCard({
  title,
  value,
  hint,
  icon,
}: {
  title: string;
  value: ReactNode;
  hint?: string;
  icon?: ReactNode;
}) {
  return (
    <article className="admin-metric" dir="rtl" lang="ar">
      {icon}
      <div className="admin-metric-body">
        <span>{title}</span>
        <strong className="admin-metric-value">{value}</strong>
        {hint ? <small>{hint}</small> : null}
      </div>
    </article>
  );
}

/** Local-filter helper matching production admin filter cards. */
export function FilterCard({
  title,
  subtitle,
  icon,
  children,
}: {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <SectionCard title={title} subtitle={subtitle} icon={icon}>
      <div className="pro-filter-grid">{children}</div>
    </SectionCard>
  );
}

export function MetricGrid({ children }: { children: ReactNode }) {
  return <section className="admin-metrics-grid">{children}</section>;
}

export function SectionCard({
  title,
  subtitle,
  icon,
  actions,
  children,
  className = '',
}: {
  title?: string;
  subtitle?: string;
  icon?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`section-card ${className}`.trim()}>
      {title ? (
        <div className="section-title">
          <div>
            <h2>{title}</h2>
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
          <div className="section-title-end">
            {actions ? <div className="section-actions">{actions}</div> : null}
            {icon ? <span className="section-icon">{icon}</span> : null}
          </div>
        </div>
      ) : null}
      {children}
    </section>
  );
}

export function CopyField({ label, value, badge }: { label: string; value: string; badge?: string }) {
  return (
    <article className="api-connection-card">
      <div className="api-field-head">
        <div>
          <span>{label}</span>
        </div>
        {badge ? <span className="api-field-badge">{badge}</span> : null}
      </div>
      <div className="api-code-field">
        <code>{value || '—'}</code>
        <button
          type="button"
          className="api-copy-button"
          onClick={() => {
            if (value) void navigator.clipboard?.writeText(value);
          }}
        >
          نسخ
        </button>
      </div>
    </article>
  );
}

export function rowTitle(...parts: unknown[]): string {
  return parts.map((p) => str(p, '')).filter(Boolean).join(' · ') || '—';
}
